import React from 'react'
import { CalendarRange } from 'lucide-react'
import DashboardCard, { CardSlot } from '@/components/dashboard/DashboardCard'
import { DeltaRow, percentDelta, pointsDelta, pointsFrom } from '@/components/dashboard/Delta'
import { HelpTooltip } from '@/components/HelpTooltip'
import { formatCount, formatMoney } from '@/lib/format'

/**
 * Band 1 — the headline. The WHOLE calendar month on the books: nights already
 * stayed plus confirmed nights still to come. Not a forecast, not month-to-date.
 * Every figure is rendered exactly as the backend sent it.
 */

export interface MonthComparison {
  roomsOtb: number
  capacityNights: number
  occupancyPct: number
  adr: number
  revpar: number
  revenue: number
}

export interface MonthOnTheBooksData extends MonthComparison {
  revenueToDate: number
  revenueRemaining: number
  monthLabel: string
  year: number
  daysElapsed: number
  daysRemaining: number
  currency: string
  stly: MonthComparison
  lyFinal: MonthComparison
  stlyAnchor: string
  occupancyDeltaPts: number | null
  hasStlyData: boolean
  hasLyFinalData: boolean
}

function formatDay(iso: string): string {
  const parsed = new Date(`${iso}T00:00:00`)
  if (Number.isNaN(parsed.getTime())) return iso
  return parsed.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

interface HeadlineCardProps {
  label: string
  value: string
  detail: React.ReactNode
  comparisons: React.ReactNode
  accent: string
}

function HeadlineCard({ label, value, detail, comparisons, accent }: HeadlineCardProps) {
  return (
    <DashboardCard accent={accent} bodyClassName="flex flex-col">
      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
        {label}
      </p>
      <p className="mt-2 font-display text-3xl font-semibold tracking-tight text-foreground tabular-nums">
        {value}
      </p>
      <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">{detail}</p>
      <div className="mt-auto space-y-1 pt-4">{comparisons}</div>
    </DashboardCard>
  )
}

function NoComparison({ children }: { children: React.ReactNode }) {
  return <p className="text-[11px] italic text-muted-foreground">{children}</p>
}

export default function MonthOnTheBooks({ month }: { month: MonthOnTheBooksData }) {
  const cur = month.currency
  const nights = month.daysElapsed + month.daysRemaining
  const { stly, lyFinal, hasStlyData, hasLyFinalData } = month

  // Percentage / point comparisons for the chips only. The £ figures beside them
  // are the backend's own STLY and LY-final numbers, printed verbatim.
  const occStly = hasStlyData ? (pointsFrom(month.occupancyDeltaPts) ?? pointsDelta(month.occupancyPct, stly.occupancyPct)) : null
  const occLy = hasLyFinalData ? pointsDelta(month.occupancyPct, lyFinal.occupancyPct) : null

  const cards: HeadlineCardProps[] = [
    {
      label: 'Occupancy',
      accent: 'bg-teal-500',
      value: `${month.occupancyPct}%`,
      detail: (
        <>
          {formatCount(month.roomsOtb)} of {formatCount(month.capacityNights)} room-nights sold
        </>
      ),
      comparisons: (
        <>
          {occStly && <DeltaRow delta={occStly} caption={`vs STLY ${stly.occupancyPct}%`} />}
          {occLy && <DeltaRow delta={occLy} caption={`vs last year final ${lyFinal.occupancyPct}%`} />}
          {!occStly && !occLy && <NoComparison>No year-ago occupancy to compare with yet</NoComparison>}
        </>
      ),
    },
    {
      label: 'ADR',
      accent: 'bg-indigo-500',
      value: formatMoney(month.adr, cur),
      detail: <>Realized rate across {formatCount(month.roomsOtb)} room-nights sold</>,
      comparisons: (
        <ComparisonPair
          currentStly={hasStlyData ? percentDelta(month.adr, stly.adr) : null}
          stlyCaption={`vs STLY ${formatMoney(stly.adr, cur)}`}
          currentLy={hasLyFinalData ? percentDelta(month.adr, lyFinal.adr) : null}
          lyCaption={`vs last year final ${formatMoney(lyFinal.adr, cur)}`}
        />
      ),
    },
    {
      label: 'RevPAR',
      accent: 'bg-violet-500',
      value: formatMoney(month.revpar, cur),
      detail: <>Across all {formatCount(month.capacityNights)} room-nights available</>,
      comparisons: (
        <ComparisonPair
          currentStly={hasStlyData ? percentDelta(month.revpar, stly.revpar) : null}
          stlyCaption={`vs STLY ${formatMoney(stly.revpar, cur)}`}
          currentLy={hasLyFinalData ? percentDelta(month.revpar, lyFinal.revpar) : null}
          lyCaption={`vs last year final ${formatMoney(lyFinal.revpar, cur)}`}
        />
      ),
    },
    {
      label: 'Revenue on the books',
      accent: 'bg-emerald-500',
      value: formatMoney(month.revenue, cur),
      detail: (
        <>
          {formatMoney(month.revenueToDate, cur)} already stayed
          <span className="mx-1 text-slate-300">·</span>
          {formatMoney(month.revenueRemaining, cur)} still to stay
        </>
      ),
      comparisons: (
        <ComparisonPair
          currentStly={hasStlyData ? percentDelta(month.revenue, stly.revenue) : null}
          stlyCaption={`vs STLY ${formatMoney(stly.revenue, cur)}`}
          currentLy={hasLyFinalData ? percentDelta(month.revenue, lyFinal.revenue) : null}
          lyCaption={`vs last year final ${formatMoney(lyFinal.revenue, cur)}`}
        />
      ),
    },
  ]

  return (
    <section>
      <div className="mb-3 flex flex-wrap items-end justify-between gap-x-4 gap-y-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <CalendarRange className="h-4 w-4 shrink-0 text-muted-foreground" />
            <h2 className="text-sm font-semibold text-foreground">
              This month on the books — {month.monthLabel} {month.year}
            </h2>
            <HelpTooltip
              content="The whole calendar month, not month-to-date and not a forecast: nights already stayed plus confirmed bookings for the nights still to come. STLY is the same month measured at the same point last year; last-year-final is how that month actually finished."
              learnMore="/guides/occupancy-metrics"
            />
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            All {nights} nights · {formatCount(month.daysElapsed)} stayed,{' '}
            {formatCount(month.daysRemaining)} still to sell
            <span className="mx-1.5 text-slate-300">·</span>
            STLY anchored on {formatDay(month.stlyAnchor)}
          </p>
        </div>
        <span className="shrink-0 rounded-full bg-teal-50 px-2.5 py-1 text-[11px] font-semibold text-teal-700 ring-1 ring-inset ring-teal-100">
          Confirmed bookings · whole month
        </span>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {cards.map((card, idx) => (
          <CardSlot key={card.label} delay={idx * 50}>
            <HeadlineCard {...card} />
          </CardSlot>
        ))}
      </div>
    </section>
  )
}

function ComparisonPair({
  currentStly,
  stlyCaption,
  currentLy,
  lyCaption,
}: {
  currentStly: ReturnType<typeof percentDelta>
  stlyCaption: string
  currentLy: ReturnType<typeof percentDelta>
  lyCaption: string
}) {
  if (!currentStly && !currentLy) {
    return <NoComparison>No year-ago figures to compare with yet</NoComparison>
  }
  return (
    <>
      {currentStly && <DeltaRow delta={currentStly} caption={stlyCaption} />}
      {currentLy && <DeltaRow delta={currentLy} caption={lyCaption} />}
    </>
  )
}
